import {
  Caption1,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  CalendarLtr16Regular,
  CheckboxChecked16Regular,
  Diamond12Filled,
  Flag16Filled,
  Folder16Regular,
} from "@fluentui/react-icons";
import { useState, type DragEvent } from "react";
import { dayOfMonth, type DayNumber, type ISODate } from "@/engine/date";
import {
  CALENDAR_DAY_MIN_WIDTH,
  getHolidayName,
  type CalendarEvent,
} from "@/features/calendar-view/calendar-layout";
import { formatDate, formatDateRange, formatDayNumber } from "@/lib/date-format";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  cell: {
    minHeight: "100%",
    height: "100%",
    minWidth: `${CALENDAR_DAY_MIN_WIDTH}px`,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    padding: "4px 6px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: "1px solid var(--cal-line)",
    borderBottom: "1px solid var(--cal-line)",
    position: "relative",
    transition: "background-color 0.15s ease",
    boxSizing: "border-box",
    ":last-child": {
      borderRight: "none",
    },
  },
  cellOut: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  cellNonWorking: {
    backgroundColor: "var(--cal-off)",
  },
  cellOver: {
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: `inset 0 0 0 2px ${tokens.colorBrandStroke1}`,
  },
  cellHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "22px",
    marginBottom: "2px",
    minWidth: 0,
    overflow: "hidden",
    flexShrink: 0,
    zIndex: 2,
    pointerEvents: "auto",
  },
  holidayHeaderItem: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "11px",
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightRegular,
    minWidth: 0,
    overflow: "hidden",
  },
  holidayText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dateWrap: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  firstOfMonthWrap: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    overflow: "hidden",
  },
  monthLabelText: {
    fontSize: "11px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dateNum: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    padding: "1px 4px",
    borderRadius: tokens.borderRadiusCircular,
  },
  dateNumOut: {
    color: tokens.colorNeutralForeground4,
  },
  dateCircleToday: {
    backgroundColor: tokens.colorNeutralForeground1,
    color: tokens.colorNeutralBackground1,
    fontWeight: tokens.fontWeightRegular,
    width: "18px",
    height: "18px",
    minWidth: "18px",
    minHeight: "18px",
    maxWidth: "18px",
    maxHeight: "18px",
    boxSizing: "border-box",
    padding: 0,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusCircular,
    fontSize: "11px",
    lineHeight: "16px",
  },
  dateCircleSelected: {
    width: "18px",
    height: "18px",
    minWidth: "18px",
    minHeight: "18px",
    maxWidth: "18px",
    maxHeight: "18px",
    boxSizing: "border-box",
    padding: 0,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusCircular,
    lineHeight: "16px",
    boxShadow: `0 0 0 1.5px ${tokens.colorBrandStroke1}`,
  },
  cellBottom: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    zIndex: 2,
    pointerEvents: "auto",
    paddingTop: "2px",
  },
  moreBtn: {
    fontSize: "11px",
    color: tokens.colorBrandForeground1,
    background: "transparent",
    border: "none",
    padding: "1px 4px",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: tokens.borderRadiusSmall,
    fontWeight: tokens.fontWeightRegular,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      textDecoration: "underline",
    },
  },
  popoverContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: "240px",
    maxWidth: "320px",
    padding: tokens.spacingVerticalXS,
  },
  popoverHeader: {
    fontWeight: tokens.fontWeightRegular,
    marginBottom: tokens.spacingVerticalXXS,
  },
  popRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 6px",
    borderRadius: tokens.borderRadiusSmall,
    cursor: "pointer",
    fontSize: "11px",
    lineHeight: "18px",
    userSelect: "none",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  popRowTitle: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground1,
  },
  popRowDone: {
    textDecoration: "line-through",
    opacity: 0.5,
  },
});

export interface CalendarDayCellProps {
  day: DayNumber;
  iso: ISODate;
  today: ISODate;
  monthKey: string;
  isWorking: boolean;
  hiddenEvents?: CalendarEvent[];
  isSelected?: boolean;
  onSelect?: () => void;
  isDragOver: boolean;
  onDragOver: (e: DragEvent, day: DayNumber) => void;
  onDragLeave: (e: DragEvent, day: DayNumber) => void;
  onDrop: (e: DragEvent, day: DayNumber) => void;
  onOpenEvent: (e: CalendarEvent) => void;
}

export function CalendarDayCell({
  day,
  iso,
  today,
  monthKey,
  isWorking,
  hiddenEvents = [],
  isSelected = false,
  onSelect,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpenEvent,
}: CalendarDayCellProps) {
  const styles = useStyles();
  const calendar = useProjectStore((s) => s.project.calendar);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const inMonth = iso.slice(0, 7) === monthKey;
  const isToday = iso === today;
  const dayNum = dayOfMonth(day);
  const isFirstOfMonth = dayNum === 1;

  const holiday = getHolidayName(iso, calendar);
  const monthName = isFirstOfMonth ? formatDayNumber(day, "monthName") : null;

  const renderIcon = (e: CalendarEvent) => {
    switch (e.kind) {
      case "wbs":
        return <Folder16Regular style={{ fontSize: 12, flexShrink: 0, color: "var(--cal-wbs-edge)" }} />;
      case "activity":
        return <CalendarLtr16Regular style={{ fontSize: 12, flexShrink: 0, color: "var(--cal-act-edge)" }} />;
      case "execution":
        return <CheckboxChecked16Regular style={{ fontSize: 12, flexShrink: 0, color: "var(--cal-task-edge)" }} />;
      case "milestone":
        return <Diamond12Filled style={{ fontSize: 11, flexShrink: 0, color: "var(--cal-mile-edge)" }} />;
      case "deadline":
        return <Flag16Filled style={{ fontSize: 11, flexShrink: 0, color: "var(--cal-due-edge)" }} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={mergeClasses(
        styles.cell,
        !inMonth && styles.cellOut,
        !isWorking && inMonth && styles.cellNonWorking,
        isDragOver && styles.cellOver,
      )}
      onDragOver={(e) => onDragOver(e, day)}
      onDragLeave={(e) => onDragLeave(e, day)}
      onDrop={(e) => onDrop(e, day)}
      data-date={iso}
    >
      {/* Top Header: Date number & Month name & Holiday */}
      <div className={styles.cellHeader}>
        {holiday && (
          <div className={styles.holidayHeaderItem} title={holiday}>
            <Flag16Filled style={{ fontSize: 11, color: tokens.colorPaletteGreenForeground1 }} />
            <span className={styles.holidayText}>{holiday}</span>
          </div>
        )}

        <div
          className={styles.dateWrap}
          onClick={onSelect}
          style={{ cursor: onSelect ? "pointer" : "default" }}
        >
          {isFirstOfMonth ? (
            <div className={styles.firstOfMonthWrap}>
              <span className={styles.monthLabelText}>{monthName}</span>
              <span
                className={mergeClasses(
                  isToday ? styles.dateCircleToday : styles.dateNum,
                  isSelected && !isToday && styles.dateCircleSelected,
                )}
              >
                1
              </span>
            </div>
          ) : isToday ? (
            <span
              className={mergeClasses(
                styles.dateCircleToday,
                isSelected && styles.dateCircleSelected,
              )}
            >
              {dayNum}
            </span>
          ) : (
            <span
              className={mergeClasses(
                styles.dateNum,
                !inMonth && styles.dateNumOut,
                isSelected && styles.dateCircleSelected,
              )}
            >
              {dayNum}
            </span>
          )}
        </div>
      </div>

      {/* Overflow '+N more events' Popover at bottom */}
      {hiddenEvents.length > 0 && (
        <div className={styles.cellBottom}>
          <Popover
            open={popoverOpen}
            onOpenChange={(_, d) => setPopoverOpen(d.open)}
            positioning="below-start"
            withArrow
          >
            <PopoverTrigger disableButtonEnhancement>
              <button
                type="button"
                className={styles.moreBtn}
                aria-label={`${formatDate(iso, "fullWeekday")} 항목 ${hiddenEvents.length}개 더 보기`}
              >
                +{hiddenEvents.length}개 더보기
              </button>
            </PopoverTrigger>
            <PopoverSurface>
              <div className={styles.popoverContent}>
                <Caption1 className={styles.popoverHeader}>{formatDate(iso, "fullWeekday")}</Caption1>
                {hiddenEvents.map((e) => (
                  <div
                    key={e.id}
                    role="button"
                    tabIndex={0}
                    className={mergeClasses(styles.popRow, e.done && styles.popRowDone)}
                    onClick={() => {
                      setPopoverOpen(false);
                      onOpenEvent(e);
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        setPopoverOpen(false);
                        onOpenEvent(e);
                      }
                    }}
                  >
                    {renderIcon(e)}
                    <span className={styles.popRowTitle}>
                      {e.wbs && e.kind !== "execution" ? `${e.wbs} ` : ""}
                      {e.title}
                    </span>
                    <Caption1 style={{ fontSize: 10, color: tokens.colorNeutralForeground4, flexShrink: 0 }}>
                      {formatDateRange(e.start, e.end, "short")}
                    </Caption1>
                  </div>
                ))}
              </div>
            </PopoverSurface>
          </Popover>
        </div>
      )}
    </div>
  );
}
